import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Avatar
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import SearchIcon from '@mui/icons-material/Search'
import PersonIcon from '@mui/icons-material/Person'
import { useEnrichedPMLeads } from '../hooks/usePMLeads'

interface PMLeadFilterProps {
  selectedPMLeads: string[]
  onPMLeadsChange: (leads: string[]) => void
  disabled?: boolean
}

interface EnrichedPMLead {
  id: string
  name: string
  displayName: string
  email?: string | null
  count?: number
  workloadLevel?: 'low' | 'medium' | 'high' | 'overloaded'
  workloadColor?: string
  initials?: string
  hasEmail?: boolean
}

const PMLeadFilter: React.FC<PMLeadFilterProps> = ({
  selectedPMLeads,
  onPMLeadsChange,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const { data: leads, isLoading, error } = useEnrichedPMLeads()

  // Debounce search term to avoid immediate filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])



  const filteredLeads = React.useMemo(() => {
    if (!leads) return []

    let filtered = leads
    
    // Apply search filter using debounced search term
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase()
      filtered = leads.filter(lead =>
        lead.name.toLowerCase().includes(term) ||
        lead.displayName.toLowerCase().includes(term) ||
        (lead.email && lead.email.toLowerCase().includes(term))
      )
    }

    // Sort by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name))
  }, [leads, debouncedSearchTerm])

  // Handle dropdown open
  const handleOpen = () => {
    setIsOpen(true)
    // Auto-focus on search input when dropdown opens
    setTimeout(() => {
      const searchInput = document.querySelector('#pm-lead-search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  // Clear search when dropdown closes
  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    onPMLeadsChange(typeof value === 'string' ? value.split(',') : value)
  }

  const handleRemovePMLead = (leadToRemove: string, event: React.MouseEvent) => {
    event.stopPropagation()
    onPMLeadsChange(selectedPMLeads.filter(lead => lead !== leadToRemove))
  }

  const handleClearAll = (event: React.MouseEvent) => {
    event.stopPropagation()
    onPMLeadsChange([])
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const getWorkloadIndicator = (lead: EnrichedPMLead) => {
    if (!lead.count) return null
    
    const level = lead.workloadLevel || 'low'
    const color = lead.workloadColor || '#4caf50'
    
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          marginLeft: 1,
          flexShrink: 0
        }}
        title={`Workload: ${level} (${lead.count} initiatives)`}
      />
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load PM Leads: {error.message}
      </Alert>
    )
  }

  return (
    <Box sx={{
      minWidth: 180, // Reduced from 200 to 180
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    }}> {/* Dynamic width based on content */}
      <FormControl
        fullWidth
        size="small"
        disabled={disabled}
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'baseline'
          }
        }}
      >
        <InputLabel id="pm-lead-filter-label" sx={{ fontSize: '0.8rem' }}>PM Lead</InputLabel>
        <Select
          labelId="pm-lead-filter-label"
          id="pm-lead-filter"
          multiple
          value={selectedPMLeads}
          onChange={handleChange}
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          label="PM Lead"
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}> {/* Reduced gap from 0.5 to 0.25 */}
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  size="small"
                  onDelete={(event) => handleRemovePMLead(value, event)}
                  onMouseDown={(event) => event.stopPropagation()}
                  avatar={
                    <Avatar sx={{ width: 18, height: 18, fontSize: '0.65rem' }}> {/* Reduced avatar size */}
                      {leads?.find(lead => lead.name === value)?.initials || value.charAt(0)}
                    </Avatar>
                  }
                  sx={{
                    height: 20, // Slightly smaller chip height
                    fontSize: '0.75rem' // Smaller font size
                  }}
                />
              ))}

            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 400,
                minWidth: 'auto', // Dynamic width based on content
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {/* Search input */}
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              id="pm-lead-search-input"
              fullWidth
              size="small"
              placeholder="Search PM Leads..."
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Allow arrow keys to navigate through menu items
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  return // Don't stop propagation for arrow keys
                }
                e.stopPropagation()
              }}
            />
          </Box>

          {/* Clear all option */}
          {selectedPMLeads.length > 0 && (
            <MenuItem
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleClearAll()
              }}
              sx={{
                fontStyle: 'italic',
                color: 'primary.main',
                borderBottom: '1px solid',
                borderColor: 'divider',
                mb: 1
              }}
            >
              Clear all selections
            </MenuItem>
          )}

          {/* Loading state */}
          {isLoading && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading PM Leads...</Typography>
              </Box>
            </MenuItem>
          )}

          {/* No results */}
          {!isLoading && filteredLeads.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {debouncedSearchTerm ? `No PM Leads found for "${debouncedSearchTerm}"` : 'No PM Leads available'}
              </Typography>
            </MenuItem>
          )}

          {/* PM Lead options */}
          {filteredLeads.map((lead) => (
            <MenuItem key={lead.id} value={lead.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                  {lead.initials || lead.name.charAt(0)}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {lead.name}
                  </Typography>
                  {lead.email && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {lead.email}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {lead.count !== undefined && (
                    <Chip
                      label={lead.count}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        ml: 1,
                        fontSize: '0.75rem',
                        height: '20px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                  {getWorkloadIndicator(lead)}
                </Box>
              </Box>
            </MenuItem>
          ))}


        </Select>
      </FormControl>
    </Box>
  )
}

export default PMLeadFilter
